<script setup lang="ts">
    import { ListChecks, X } from 'lucide-vue-next';
    import {
        PopoverClose,
        PopoverContent,
        PopoverPortal,
        PopoverRoot,
        PopoverTrigger,
    } from 'reka-ui';
    import { useI18n } from 'vue-i18n';
    import { computed, ref } from 'vue';
    import { Button } from '@/components/ui/button';
    import OnboardingGuideItem from './OnboardingGuideItem.vue';
    import OnboardingAssistantModal from './OnboardingAssistantModal.vue';
    import type { OnboardingStep } from '@/types';

    const { t } = useI18n();

    const props = defineProps<{
        onboardingSteps: OnboardingStep[];
        companyId: number;
    }>();

    const steps = computed(() => {
        return [...props.onboardingSteps].sort((a, b) => {
            if (a.is_completed && !b.is_completed) return 1;
            if (!a.is_completed && b.is_completed) return -1;
            return 0;
        });
    });

    const open = ref(false);
    const assistantOpen = ref(false);

    const openModal = () => {
        assistantOpen.value = true;
        open.value = false;
    };

    defineExpose({
        assistantOpen,
    });
</script>

<template>
    <PopoverRoot :open="open">
        <PopoverTrigger
            class="text-foreground hover:text-accent focus:text-accent inline-flex cursor-pointer items-center justify-center"
            @click="open = !open"
        >
            <ListChecks />
        </PopoverTrigger>
        <PopoverPortal>
            <PopoverContent
                side="bottom"
                :side-offset="5"
                avoid-collisions
                :collision-padding="10"
                class="animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-60 rounded border bg-white p-4 shadow-sm will-change-[transform,opacity]"
            >
                <div class="flex flex-col items-center gap-5">
                    <h3 class="text-muted-foreground w-full text-xs font-medium">
                        {{ t('onboarding.guide.title') }}
                    </h3>
                    <div class="grid auto-cols-fr grid-flow-col">
                        <OnboardingGuideItem
                            v-for="step in steps"
                            :key="step.id"
                            :onboarding-step="step"
                        />
                    </div>
                    <Button
                        variant="default"
                        @click="openModal"
                    >
                        {{ t('onboarding.complete') }}
                    </Button>
                </div>
                <PopoverClose
                    class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                    aria-label="Close"
                    tabindex="-1"
                    @click="open = false"
                >
                    <X />
                </PopoverClose>
            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
    <OnboardingAssistantModal
        :onboarding-steps="steps"
        v-model="assistantOpen"
        :company-id="companyId"
    />
</template>
